'use client';

import { type FC } from 'react';

import MermaidDiagram from '@/components/Diagram/MermaidDiagram';
import { cn } from '@/lib/utils';
import { type MermaidDiagram as MermaidDiagramType, type MermaidLayout } from '@/data/blog/types';
import { MermaidFullscreenOverlay } from './MermaidFullscreenOverlay';
import { useMermaidFullscreen } from './useMermaidFullscreen';

interface MermaidRowProps {
  layout: MermaidLayout;
  diagrams: MermaidDiagramType[];
}

export const MermaidRow: FC<MermaidRowProps> = ({ layout, diagrams }) => {
  if (!diagrams || diagrams.length === 0) return null;

  const { activeDiagram, open, close } = useMermaidFullscreen();

  const gridClass =
    {
      single: 'grid-cols-1',
      double: 'grid-cols-1 md:grid-cols-2',
      full: 'grid-cols-1',
    }[layout] ?? 'grid-cols-1';

  const aspectClass = 'aspect-square';

  return (
    <>
      <div className={cn('grid gap-6', gridClass)}>
        {diagrams.map((diagram, index) => {
          const variant = diagram.kind ?? 'mermaid';
          const isVideo = variant === 'video';

          if (isVideo) {
            const hasExplicitSources = Boolean(diagram.sources && diagram.sources.length > 0);
            const videoSources = hasExplicitSources
              ? diagram.sources!
              : diagram.src
                ? [{ src: diagram.src }]
                : [];

            if (videoSources.length === 0) {
              console.warn('[MermaidRow] Video diagram missing source', { diagram, index });
              return null;
            }

            return (
              <figure
                key={index}
                className={cn(
                  'group relative flex w-full flex-col overflow-hidden rounded-[30px] border border-zinc-200/60 bg-white shadow-[0_20px_45px_rgba(15,15,35,0.12)] transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] hover:-translate-y-0.5 dark:border-white/10 dark:bg-neutral-900/60',
                  aspectClass,
                )}
              >
                <div className="relative flex h-full w-full items-center justify-center">
                  <video
                    className="h-full w-full object-cover object-center transition-transform duration-[1100ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03]"
                    autoPlay={diagram.autoPlay ?? true}
                    loop={diagram.loop ?? true}
                    muted={diagram.muted ?? true}
                    playsInline={diagram.playsInline ?? true}
                    aria-label={diagram.title ?? diagram.description ?? 'Solution planning video'}
                  >
                    {videoSources.map(({ src, type }) => (
                      <source key={src} src={src} type={type} />
                    ))}
                  </video>

                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-neutral-950/25 to-transparent opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100" />

                  {(diagram.title || diagram.description) && (
                    <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-6 pb-6 pt-8 text-sm text-white/90 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                      {diagram.title ? <p className="font-semibold uppercase tracking-[0.22em]">{diagram.title}</p> : null}
                      {diagram.description ? (
                        <p className="mt-2 text-sm font-normal tracking-normal text-white/80">{diagram.description}</p>
                      ) : null}
                    </figcaption>
                  )}
                </div>
              </figure>
            );
          }

          if (!diagram.code) {
            console.warn('[MermaidRow] Mermaid diagram missing code', { diagram, index });
            return null;
          }

          return (
            <figure
              key={index}
                className={cn(
                  'group relative flex w-full flex-col overflow-hidden rounded-[30px] border border-zinc-200/60 bg-white shadow-[0_20px_45px_rgba(15,15,35,0.12)] transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)] hover:-translate-y-0.5 dark:border-white/10 dark:bg-neutral-900/60',
                  aspectClass,
                )}
              >
              <div className="relative flex h-full w-full items-center justify-center">
                <MermaidDiagram
                  className="h-full w-full"
                  code={diagram.code}
                  description={diagram.description}
                  padding="none"
                  surface="flat"
                  theme={diagram.theme}
                  title={diagram.title}
                />

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-neutral-950/25 to-transparent opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100" />

                {(diagram.title || diagram.description) && (
                  <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-6 pb-6 pt-8 text-sm text-white/90 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                    {diagram.title ? <p className="font-semibold uppercase tracking-[0.22em]">{diagram.title}</p> : null}
                    {diagram.description ? (
                      <p className="mt-2 text-sm font-normal tracking-normal text-white/80">{diagram.description}</p>
                    ) : null}
                  </figcaption>
                )}

                <button
                  type="button"
                  onClick={() => open(diagram)}
                  className="absolute right-5 top-5 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-lg transition hover:bg-white md:opacity-0 md:group-hover:opacity-100 dark:bg-neutral-900/90 dark:text-white dark:hover:bg-neutral-800"
                  aria-label="Open diagram fullscreen"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    className="size-4"
                  >
                    <path
                      d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </figure>
          );
        })}
      </div>

      <MermaidFullscreenOverlay diagram={activeDiagram} onClose={close} />
    </>
  );
};

export default MermaidRow;
